import { useState, useEffect } from 'react';
import type { DeviceState, AppScreen } from '@/types';
import { clearDeviceState, loadDeviceState } from '@/core/device-identity';
import { initializeCache } from '@/playback/media-cache';
import ProvisionScreen from '@/screens/ProvisionScreen';
import PlayerScreen from '@/screens/PlayerScreen';
import OfflineScreen from '@/screens/OfflineScreen';
import DiagnosticScreen from '@/screens/DiagnosticScreen';
import SafeScreen from '@/screens/SafeScreen';
import { setWebUpdaterBoardId } from '@/core/web-updater';
import { setSentryContext } from '@/core/sentry';
import {
  clearSafeMode,
  markBootSucceeded,
  readSafeMode,
} from '@/health/watchdog';
import { logger } from '@/core/logger';
import { markBootComplete } from '@/core/perf';

/**
 * URL-based escape hatch: visiting `?reset=true` (or `#reset`) wipes
 * all persistent state and reloads to the bare provisioning flow.
 * Useful when a TV is physically far from a developer — operator can
 * simply re-enter the URL with the suffix from a remote URL Launcher
 * tool, no Web Inspector needed.
 */
function checkUrlResetEscape(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    if (
      url.searchParams.has('reset') ||
      url.hash === '#reset' ||
      url.hash === '#factory-reset'
    ) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

async function runFactoryReset(): Promise<void> {
  logger.warn('Factory reset triggered');
  try {
    await clearDeviceState();
  } catch {
    // ignore — moving on regardless
  }
  try {
    localStorage.clear();
  } catch {
    // ignore
  }
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
  // Drop all Cache Storage entries — media + storage backends rebuild
  // fresh on next boot.
  if (typeof caches !== 'undefined') {
    try {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    } catch {
      // ignore
    }
  }
  // Always reload at the bare URL — strip any ?reset query so we don't
  // loop the escape hatch forever.
  try {
    const u = new URL(window.location.href);
    u.search = '';
    u.hash = '';
    window.location.replace(u.toString());
  } catch {
    window.location.reload();
  }
}

export default function App() {
  const [screen, setScreen] = useState<AppScreen>('loading');
  const [deviceState, setDeviceState] = useState<DeviceState>({
    boardId: null,
    boardCode: null,
    provisioned: false,
  });

  useEffect(() => {
    // URL escape — runs before anything else hydrates state.
    if (checkUrlResetEscape()) {
      void runFactoryReset();
      return;
    }

    // Safe mode short-circuits the regular boot path. We DON'T touch
    // the media cache, plan fetch, or any other subsystem that might
    // have caused the failure that landed us here.
    if (readSafeMode()) {
      setScreen('safe');
      // Safe screen counts as "stable" for the boot watchdog so we
      // don't escalate further.
      markBootSucceeded();
      return;
    }

    // Restore the device identity (boardId/code from localStorage) and
    // hydrate the media cache index from Cache Storage in parallel. We
    // gate the screen switch on BOTH so that by the time PlayerScreen
    // mounts, getAllCachedPaths() returns real entries and the cached
    // plan can render content immediately — no flash of "Idle" while
    // the cache index loads from disk.
    Promise.all([loadDeviceState(), initializeCache()])
      .then(([state]) => {
        setDeviceState(state);
        setScreen(state.provisioned ? 'player' : 'provision');
        // We're on the first user-facing screen — boot has stabilised.
        markBootSucceeded();
        markBootComplete();
      })
      .catch((err) => {
        logger.error('App boot failed', { err: String(err) });
        // Leave screen as 'loading' — boot watchdog will fire and
        // either reload or enter safe mode.
      });
  }, []);

  if (screen === 'loading') {
    return (
      <div style={styles.loading}>
        <div style={styles.logo}>S</div>
      </div>
    );
  }

  if (screen === 'safe') {
    return (
      <SafeScreen
        onRetry={() => {
          clearSafeMode();
          window.location.reload();
        }}
        onFactoryReset={() => {
          void runFactoryReset();
        }}
      />
    );
  }

  if (screen === 'provision') {
    return (
      <ProvisionScreen
        onProvisioned={(state) => {
          // Tell the auto-updater + Sentry which board we are now —
          // rollout bucketing becomes stable, crash events get the
          // boardId tag.
          if (state.boardId) {
            setWebUpdaterBoardId(state.boardId);
            setSentryContext({ boardId: state.boardId });
          }
          setDeviceState(state);
          setScreen('player');
        }}
      />
    );
  }

  if (screen === 'diagnostic') {
    return (
      <DiagnosticScreen
        deviceState={deviceState}
        onClose={() => setScreen('player')}
        onReset={() => {
          setDeviceState({
            boardId: null,
            boardCode: null,
            provisioned: false,
          });
          setScreen('provision');
        }}
      />
    );
  }

  if (screen === 'offline') {
    return <OfflineScreen />;
  }

  // Player screen
  return (
    <PlayerScreen
      deviceState={deviceState}
      onDiagnostic={() => setScreen('diagnostic')}
    />
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#0A0A0A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #FF6B2B, #FF8F5E)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 30,
    fontWeight: 700,
    animation: 'pulse 1.5s infinite',
  },
};
